import type { LeatherbackState, World } from '../types';
import { WEST_PACIFIC_LEATHERBACK, isAdult, stepLeatherbacks } from '../leatherback';
import { LEATHERBACK_SUPPLY, LEATHERBACK_TABLE } from '../data/leatherbacks/westpacific';
import { STREAM, stockStream } from '../streams';
import { EXPECTED_DRAWS, stockDraws, type PopulationDraws } from '../population';
import { civilFromEpochDay, epochDayFromCivil, epochDayOf } from '../calendar';
import { wholeSeals } from './seals';

/** The place the leatherbacks' stream is named for: their ocean, the Pacific, whose low bands they nest and feed in. */
export const LEATHERBACK_PLACE = 'pacific';

/** The leatherbacks' draws on their population's stream of the day (`stockDraws`, one population): binomial deaths and nesters on each count, Poisson draws of the day's hatchlings. */
export function leatherbackDraws(world: { seed: number; day: number }, streamOf: typeof stockStream = stockStream): PopulationDraws {
  return stockDraws(world, STREAM.leatherback, LEATHERBACK_PLACE, streamOf);
}

/** A day of the western Pacific leatherbacks (`leatherback.ts`) on their own stream of the day (`migrants.ts` steps them). */
export function stepWestPacificLeatherbacks(world: World, streamOf: typeof stockStream = stockStream): void {
  stepLeatherbacks(WEST_PACIFIC_LEATHERBACK, world.migrants.leatherback, epochDayOf(world), LEATHERBACK_SUPPLY, leatherbackDraws(world, streamOf));
}

/**
 * The leatherbacks as a world opens on a day: the table (`data/leatherbacks/`,
 * the eve of a first of January, NOAA's adult females of 2025) on the eve of
 * the opening year's, whatever the year (a world opens on 1 January 2026;
 * no scenario opens on another yet), stepped at expected values to the
 * opening day, each sex's young and adult classes and the nesters made whole
 * turtles apart (`wholeSeals`; no draw: a world's opening draws nothing), so
 * the opening's adults are its expected adults rounded (M111: rounded
 * together, a new game had 1,053 adult females for NOAA's 1,054).
 */
export function openingLeatherbacks(epochDay: number): LeatherbackState {
  const life = WEST_PACIFIC_LEATHERBACK;
  const w: LeatherbackState = { females: [...LEATHERBACK_TABLE.females], males: [...LEATHERBACK_TABLE.males], nesters: [...LEATHERBACK_TABLE.nesters] };
  for (let d = epochDayFromCivil(civilFromEpochDay(epochDay).year, 1, 1); d <= epochDay; d++) stepLeatherbacks(life, w, d, LEATHERBACK_SUPPLY, EXPECTED_DRAWS);
  const adultFrom = isAdult(life, life.matureYears, epochDay) ? life.matureYears : life.matureYears + 1;
  const whole = (xs: number[]): number[] => [...wholeSeals(xs.slice(0, adultFrom)), ...wholeSeals(xs.slice(adultFrom))];
  return { females: whole(w.females), males: whole(w.males), nesters: w.nesters.map(Math.round) };
}
